import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import { z } from "zod";
import { useShareHolderDetails } from "../ShareHolderDetail";
import useFileUpload from "~/utils/useFileUpload";
import { getPeriodFromFileYearMonth } from "~/utils/fileUtils";
import { useSelector } from "react-redux";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import {
  buildCategoryFoldersFromDocuments,
  getDocumentCategoryCode,
} from "~/utils/categoryFolders";

const useShareholderProfileAttachments = (entityData, isRegDataLoading) => {
  const [filesData, setFilesdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const { regData } = useShareHolderDetails();
  const [successMessage, setSuccessMessage] = useState("");
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [restoreMessage, setRstoreMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { id: userId, role } = useSelector((state) => state.login);

  const { shareholderId: id, entityId } = useParams();
  const [filterData, setFilterData] = useState({});
  const [categoryFolders, setCategoryFolders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chipdata, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();

  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const navigate = useNavigate();

  useEffect(() => {
    const hasEntityPlusAccess = entityData?.active_services?.some(
      (e) => e.name === "Entities +",
    );
    if (!hasEntityPlusAccess && !isRegDataLoading) {
      navigate("/forbidden");
    }
  }, [entityData]);

  const fetchShareholderAttachments = async (skipCache = false) => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "SHAREHOLDER" },
      skipCache,
    });
    // const baseUrl = API_ROUTES_V2.FETCH_DOCUMENTS.replace("{type}", "SHAREHOLDER").replace("{id}", id);
    // const entityParam = entityId || (entityData && entityData.id) || "";
    // const url = entityParam ? `${baseUrl}&entity_id=${encodeURIComponent(entityParam)}` : baseUrl;
    // const { data } = await apiService(url, "GET", { skipCache });
    setFilesdata(data);
    setCategoryFolders(buildCategoryFoldersFromDocuments(data));
    setIsDataAvailable(data.length > 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchShareholderAttachments();
  }, []);

  const handleChangePage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const uploadFileHandler = async (files) => {
    setIsFileUploaded(false);
    try {
      setLoading(true);
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));

        tags = {
          entity_id: uuidToUlid(entityData?.id),
          country: entityData?.country,
          temporary_file: "false",
          shareHolder: uuidToUlid(id),
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
          ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
            period: getPeriodFromFileYearMonth(file.fileYearMonth),
          }),
        };
        const version = `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "SHAREHOLDER",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const uploadData = await uploadFile(file, file.fileName, {
          entityId: entityData?.id,
          serviceLineCode: "ENTITIES_PLUS",
          fileName: file.fileName,
        });
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({
          url: uploadData.url,
          fileName: uploadData.id,
          name: file.fileName,
        });
        await updateTag(uploadData.id, tags, metadata);
      }
      const res = await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
        body: { attachments },
        replace: { id, type: "SHAREHOLDERS" },
        schema: z.object({
          id: z.string().uuid(),
          type: z.enum(["SHAREHOLDERS"]),
        }),
      });
      if (res.status) {
        // Introduced a delay to allow time for the virus scan to complete after file upload,
        // ensuring the document list fetch reflects the latest and clean files.
        setTimeout(async () => {
          setIsFileUploaded(true);
          setFilesdata(files);
          setSuccessMessage("File uploaded successfully");
          fetchShareholderAttachments(true); // skipCache after upload
        }, 2000);
      }
    } catch (e) {
      console.error(e);
      setIsFileUploaded(false);
    } finally {
      setOpenDialog(false);
    }
  };

  const getNameForFile = () => {
    const name =
      regData?.type === "Individual"
        ? `${regData?.name}${regData?.last_name ? " " + regData?.last_name : ""}`
        : `${regData?.legal_name}`;
    return { name };
  };

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");
    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: { relatedId: id, relatedType: "SHAREHOLDERS" },
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchShareholderAttachments(true); // skipCache after delete
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };

  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRstoreMessage("");
    setLoading(true);
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
    });
    if (status) {
      setTimeout(async () => {
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchShareholderAttachments(true); // skipCache after restore
        }
        setRstoreMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: id,
      type: "SHAREHOLDER",
    });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApply();
    } else {
      fetchShareholderAttachments();
    }
  }, [filterData]);
  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
    }
  };

  const handleCategoryFolderClick = (folder) => {
    setSelectedCategory(folder);
  };

  const displayFiles = selectedCategory
    ? filesData.filter(
        (f) => getDocumentCategoryCode(f) === selectedCategory.code,
      )
    : filesData;

  return {
    filesData,
    loading,
    first,
    rows,
    onPageChange: handleChangePage,
    isDataAvailable,
    handleOpenDialog,
    setOpenDialog,
    openDialog,
    uploadFileHandler,
    shareHolderData: regData,
    fileSupported,
    isFileUploaded,
    setIsFileUploaded,
    successMessage,
    setSuccessMessage,
    getNameForFile,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    handleConfirmDelete,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleDeleteDocument,
    handleRestoreAttachment,
    temporaryMessage,
    isAttachmentLoader,
    setTemporaryMessage,
    restoreMessage,
    setRstoreMessage,
    role,
    selectedItems,
    filterData,
    setFilterData,
    chipdata,
    setChipData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    fetchShareholderAttachments,
    categoryFolders,
    selectedCategory,
    setSelectedCategory,
    handleCategoryFolderClick,
    displayFiles,
  };
};

export default useShareholderProfileAttachments;
